import React, { Component } from 'react';
import withStyles from '@mui/styles/withStyles';
import { Grid, Checkbox } from '@mui/material';
import compose from 'recompose/compose';
import { change, Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import DetailField from 'components/form/field/field';
import { renderDatePicker } from 'components/form/datepicker/datetime-picker';
import { ADD_FINANCIAL_ASSISTANCE_FORM } from 'constants/index';
import { addFinancialAssistanceTask, fetchFinancialAssistance } from 'actions/action-financials';
import {
  validateDate,
  validateDollarValue,
  required,
  validateAlphaNumeric,
  length6,
  maxLength20,
} from 'components/form/validation/validation';
import { stripFormattedDollarValue } from 'services/utils/formatting';
import { convertToArborDate } from 'models/time/arbor-date';
import ConfirmationPanel from 'components/form/confirmation/confirmation-panel';
import {
  renderDropdown,
  renderTextField,
  renderMoneyTextField,
  renderPhoneTextField,
} from 'components/form/field/redux-field';
import { styles } from '../../task-detail-styles';

import { DispensingStatusValue } from '../../../../constants/enums';

import { FaOcrInput } from '../../../../components/fa-ocr-input/fa-ocr-input';

const form = `${ADD_FINANCIAL_ASSISTANCE_FORM}_COPAY_CARD`;
class AddCopayCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: null,
      therapyId: null,
      noGroupIdCheckbox: false,
      noPCNCheckbox: false,
      dooTherapy: props.therapy.dispensing_status_id === DispensingStatusValue.OptOut,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleGroupIdCheckbox = this.handleGroupIdCheckbox.bind(this);
    this.handleNoPCNCheckbox = this.handleNoPCNCheckbox.bind(this);
  }

  // Handles the submit functionality. Right now it sets some fields to null for simplicity's sake
  handleSubmit(values) {
    const {
      addFinancialAssistanceTask, // eslint-disable-line
      reset,
      therapy,
      cancel,
      task,
      selectedPatientId,
      patientId,
      categoryId,
    } = this.props;
    const { noGroupIdCheckbox, noPCNCheckbox, dooTherapy } = this.state;
    const payload = {
      ...values,
      patient_id: patientId || selectedPatientId,
      therapy_id: therapy ? therapy.id : values.therapy_id,
      task_fa_id: task ? task.id : null,
      financial_assistance_type: categoryId,
      status: values.status,
      annual_limit: values.annual_limit ? stripFormattedDollarValue(values.annual_limit) : null,
      award_amount: values.award_amount ? stripFormattedDollarValue(values.award_amount) : null,
      remaining_balance: values.remaining_balance
        ? stripFormattedDollarValue(values.remaining_balance)
        : null,
      expiration_date: values.expiration_date
        ? convertToArborDate(values.expiration_date).getUtcDatetime()
        : null,
      bin: values.bin,
      pcn: noPCNCheckbox ? null : values.pcn,
      group_id: noGroupIdCheckbox ? null : values.group_id,
      phone_number: values.phone_number,
      instructions: values.instructions,
      member_id: values.member_id,
      copay: values.copay ? stripFormattedDollarValue(values.copay) : null,
    };
    addFinancialAssistanceTask(payload);
    cancel();
    reset();
  }

  handleGroupIdCheckbox() {
    this.setState(prevState => ({
      noGroupIdCheckbox: !prevState.noGroupIdCheckbox,
    }));
  }

  handleNoPCNCheckbox() {
    this.setState(prevState => ({
      noPCNCheckbox: !prevState.noPCNCheckbox,
    }));
  }

  render() {
    const {
      handleSubmit,
      pristine,
      submitting,
      classes,
      cancel,
      isPharmacyBenefit,
      addingFromFinancials,
      therapies,
      task,
      dispatch,
    } = this.props;

    const { status, therapyId, noGroupIdCheckbox, noPCNCheckbox, dooTherapy } = this.state;
    const idSuffix = task && task.therapy_id && task.id ? `_${task.therapy_id}_${task.id}` : '';
    const isRequiredMark = dooTherapy ? '' : '*';
    return (
      <Grid container justifyContent="center" spacing={7}>
        <Grid item xs={12}>
          <form className={classes.addFormContainer} autoComplete="off">
            <Grid container>
              {isPharmacyBenefit && (
                <FaOcrInput
                  handleReset={() => {
                    this.setState({
                      usingOcrData: false,
                    });
                    dispatch(change(form, 'bin', ''));
                    dispatch(change(form, 'group_id', ''));
                    dispatch(change(form, 'pcn', ''));
                    dispatch(change(form, 'member_id', ''));
                  }}
                  handleData={ocrData => {
                    this.setState({
                      usingOcrData: true,
                    });
                    dispatch(change(form, 'bin', ocrData.data.bin));
                    dispatch(change(form, 'group_id', ocrData.data.group));
                    dispatch(change(form, 'pcn', ocrData.data.pcn));
                    dispatch(change(form, 'member_id', ocrData.data.memberId));
                  }}
                />
              )}
              {addingFromFinancials && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="therapy_id"
                    label="Therapy *"
                    validate={[required]}
                    component={renderDropdown}
                    fields={Object.values(therapies).map(t => ({
                      label: t.drug_name,
                      value: t.id,
                    }))}
                    state={therapyId}
                    id={`add_FA_copay_therapy_id${idSuffix}`}
                  />
                </Grid>
              )}
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="status"
                  label="Status"
                  component={renderDropdown}
                  fields={[
                    { label: 'Active', value: 1 },
                    { label: 'Inactive', value: 0 },
                  ]}
                  state={status}
                  id={`add_FA_copay_status${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="award_amount"
                  label="Award Amount"
                  component={renderMoneyTextField}
                  validate={[validateDollarValue]}
                  id={`add_FA_copay_award_amount${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="annual_limit"
                  label="Annual Limit"
                  component={renderMoneyTextField}
                  validate={[validateDollarValue]}
                  id={`add_FA_copay_annual_limit${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="expiration_date"
                  label={!dooTherapy ? 'Expiration Date *' : 'Expiration Date'}
                  component={renderDatePicker}
                  validate={!dooTherapy ? [validateDate, required] : null}
                  id={`add_FA_copay_expiration_date${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="remaining_balance"
                  label="Remaining Balance"
                  component={renderMoneyTextField}
                  validate={[validateDollarValue]}
                  id={`add_FA_copay_remaining_balanace${idSuffix}`}
                />
              </Grid>
              {isPharmacyBenefit && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="bin"
                    label={
                      !this.state.usingOcrData
                        ? `BIN ${isRequiredMark}`
                        : `BIN ${isRequiredMark} [OCR]`
                    }
                    component={renderTextField}
                    validate={!dooTherapy ? [required, validateAlphaNumeric, length6] : null}
                    id={`add_FA_copay_bin${idSuffix}`}
                  />
                </Grid>
              )}
              {isPharmacyBenefit && (
                <Grid item xs={6}>
                  <Grid container>
                    <Grid item xs={6} className={classes.fieldContainer}>
                      <Field
                        name="pcn"
                        label={
                          !noPCNCheckbox
                            ? `PCN ${isRequiredMark} ${!this.state.usingOcrData ? '' : '[OCR]'}`
                            : `PCN ${!this.state.usingOcrData ? '' : '[OCR]'}`
                        }
                        component={renderTextField}
                        disabled={noPCNCheckbox}
                        validate={!noPCNCheckbox && !dooTherapy ? [required, maxLength20] : null}
                        id={`add_FA_copay_pcn_${idSuffix}`}
                      />
                    </Grid>
                    <Grid item xs={6} className={classes.fieldContainer}>
                      <Grid container alignItems="center" justifyContent="flex-start">
                        <Checkbox
                          checked={noPCNCheckbox}
                          color="primary"
                          onChange={this.handleNoPCNCheckbox}
                          value="checkedPCN"
                          label="Secondary"
                          id={`add_FA_copay_no_pcn${idSuffix}`}
                        />
                        <DetailField defaultValue="No PCN" />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {isPharmacyBenefit && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="member_id"
                    label={
                      !dooTherapy
                        ? `Member ID * ${!this.state.usingOcrData ? '' : '[OCR]'}`
                        : `Member ID ${!this.state.usingOcrData ? '' : '[OCR]'}`
                    }
                    component={renderTextField}
                    validate={!dooTherapy ? [required] : null}
                    id={`add_FA_copay_member_id${idSuffix}`}
                  />
                </Grid>
              )}
              {isPharmacyBenefit && (
                <Grid item xs={6}>
                  <Grid container>
                    <Grid item xs={6} className={classes.fieldContainer}>
                      <Field
                        name="group_id"
                        label={
                          !noGroupIdCheckbox
                            ? `Group ID ${isRequiredMark} ${
                                !this.state.usingOcrData ? '' : '[OCR]'
                              }`
                            : `Group ID ${!this.state.usingOcrData ? '' : '[OCR]'}`
                        }
                        validate={!noGroupIdCheckbox && !dooTherapy ? [required] : null}
                        component={renderTextField}
                        disabled={noGroupIdCheckbox}
                        id={`add_FA_copay_group_id${idSuffix}`}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Grid container alignItems="center" justifyContent="flex-start">
                        <Checkbox
                          checked={noGroupIdCheckbox}
                          color="primary"
                          onChange={this.handleGroupIdCheckbox}
                          value="checkedGroupId"
                          label="Secondary"
                          disabled={dooTherapy}
                          id={`add_FA_copay_no_group_id${idSuffix}`}
                        />
                        <DetailField defaultValue="No Group Id" />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {isPharmacyBenefit && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="phone_number"
                    label="Phone Number"
                    component={renderPhoneTextField}
                    id={`add_FA_copay_phone_number${idSuffix}`}
                  />
                </Grid>
              )}

              {!isPharmacyBenefit && (
                <Grid item xs={3} className={classes.fieldContainer}>
                  <Field
                    name="member_id"
                    component={renderTextField}
                    label={!dooTherapy ? 'Member ID *' : 'Member ID'}
                    id={`add_FA_copay_member_id_not_benefit${idSuffix}`}
                  />
                </Grid>
              )}
              <Grid item xs={6} className={classes.fieldContainer}>
                <Field
                  name="instructions"
                  label="Processing Instructions"
                  component={renderTextField}
                  multiline
                  rows={3}
                  id={`add_FA_copay_instructions${idSuffix}`}
                />
              </Grid>
              <Grid item xs={3} className={classes.fieldContainer}>
                <Field
                  name="copay"
                  label="Copay"
                  component={renderMoneyTextField}
                  id={`add_FA_copay_copay${idSuffix}`}
                  decimalScale={2}
                  fixedDecimalScale
                  decimalSeparator="."
                />
              </Grid>
            </Grid>
            <ConfirmationPanel
              cancelButtonName="add_task_cancel_button"
              submitButtonName="add_task_submit_button"
              handleSubmit={handleSubmit(this.handleSubmit)}
              handleCancel={cancel}
              disableSubmit={pristine || submitting}
            />
          </form>
        </Grid>
      </Grid>
    );
  }
}

function mapStateToProps(state) {
  const { selectedPatientId, therapies } = state;
  return {
    selectedPatientId,
    therapies: therapies.data,
    initialValues: {
      patient_id: null,
      therapy_id: null,
      status: 1,
      annual_limit: null,
      expiration_date: null,
      bin: null,
      pcn: null,
      group_id: null,
      phone_number: null,
      details: null,
      task_fa_id: null,
      award_amount: null,
      copay: null,
    },
  };
}

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(mapStateToProps, {
    addFinancialAssistanceTask,
    fetchFinancialAssistance,
  }),
)(reduxForm({ form })(AddCopayCard));
